/**
 * The people a step names (an Approval step's approvers, a Notify or Send
 * email step's recipients): chosen from the organization's members, or typed
 * when the list cannot be read. The server checks membership again when the
 * step runs, so a typed address that is not a member reaches no one.
 *
 * @module components/workflows/PeoplePicker
 */
import { PlusIcon, XIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";

/** A member who can be named on a step. */
export interface MemberChoice {
  readonly email: string;
  readonly name: string | null;
}

export function PeoplePicker({
  label,
  people,
  members,
  limit,
  note,
  role,
  onChange,
}: {
  readonly label: string;
  readonly people: ReadonlyArray<string>;
  /** The organization's members; null when they cannot be read. */
  readonly members: ReadonlyArray<MemberChoice> | null;
  /** The most the step can name; past that the save would be refused. */
  readonly limit: number;
  readonly note: ReactNode;
  /** Names the picker's parts for tests: `data-workflow-<role>s`, `data-workflow-<role>=<email>`, … */
  readonly role: "approver" | "recipient";
  readonly onChange: (next: ReadonlyArray<string>) => void;
}) {
  const [typed, setTyped] = useState("");
  const chosen = new Set(people.map((email) => email.toLowerCase()));
  const addable = (members ?? []).filter((member) => !chosen.has(member.email.toLowerCase()));
  const full = people.length >= limit;
  const add = (email: string) => {
    const clean = email.trim().toLowerCase();
    if (clean && !chosen.has(clean) && !full) onChange([...people, clean]);
  };
  const nameOf = (email: string) => members?.find((member) => member.email.toLowerCase() === email.toLowerCase())?.name ?? null;
  const data = (suffix: string, value = "") => ({ [`data-workflow-${suffix}`]: value });
  return (
    <div className="space-y-1.5" {...data(`${role}s`)}>
      <span className="text-xs font-medium">{label}</span>
      {people.length > 0 ? (
        <ul className="space-y-1">
          {people.map((email) => (
            <li key={email} className="flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1 text-[12.5px]" {...data(role, email)}>
              <span className="min-w-0 flex-1 truncate">
                {nameOf(email) ? (
                  <>
                    {nameOf(email)} <span className="text-muted-foreground">{email}</span>
                  </>
                ) : (
                  email
                )}
              </span>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label={`Remove ${email}`}
                onClick={() => onChange(people.filter((entry) => entry !== email))}
              >
                <XIcon />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] text-muted-foreground">Nobody yet.</p>
      )}
      {full ? (
        <p className="text-[11px] text-muted-foreground" {...data(`${role}s-full`)}>
          That is the most one step can name ({limit}).
        </p>
      ) : null}
      {members !== null && addable.length > 0 && !full ? (
        <Select value="" onValueChange={(value) => (typeof value === "string" ? add(value) : undefined)}>
          <SelectTrigger size="sm" aria-label={`Add to ${label.toLowerCase()}`} {...data(`add-${role}`)}>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <PlusIcon className="size-3.5" />
              Add a member
            </span>
          </SelectTrigger>
          <SelectContent>
            {addable.map((member) => (
              <SelectItem key={member.email} value={member.email}>
                {member.name ? `${member.name} · ${member.email}` : member.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
      {members === null && !full ? (
        <form
          className="flex gap-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            add(typed);
            setTyped("");
          }}
        >
          <Input
            value={typed}
            type="email"
            placeholder="name@company.com"
            onChange={(event) => setTyped(event.currentTarget.value)}
            {...data(`${role}-email`)}
          />
          <Button size="sm" variant="outline" type="submit" disabled={!typed.trim()}>
            Add
          </Button>
        </form>
      ) : null}
      <span className="block text-[11px] leading-snug text-muted-foreground">{note}</span>
    </div>
  );
}
